/**
 * 案件の新規登録 (/products/manual/new・docs/features/manual-product-brand-first-flow.md §6 案D・§6.7「登録時に作る」)。
 *
 * - 入口画面: 「URLから{案件}情報を生成する」(URL を入れて読む) / 「手動で入力する」の 2 択。ブランド詳細の一覧から来た
 *   (?brand_id=) ときは入口を飛ばし、ブランド選択済みでマニュアル経路のフォームを開く (案C)。案件一覧の「1{案件}ごと登録」は
 *   2 択をモーダル (ProductEntryModal) で出し (URL 経路は読み取りの完了までモーダルの中)、選んだ経路を遷移の state で渡すので、
 *   ここでは入口を飛ばす
 * - URL 経路: 読み取りパネルをフォームの前に出し、完了したら既定採用の対象だけを下書きへ反映してフォームを開く。ブランドは
 *   一致 (高信頼) なら選択済み、候補なら利用者が選ぶ、無ければ「新規ブランドとして登録しますか」の確認を出す (無言で作らない)。
 *   パネルはフォームの上に残り、後から見直せる
 * - マニュアル経路: フォーム。ブランド欄は先頭 (案B)。「URLから読み取る」はモーダルで開ける
 * - 「登録する」で初めて INSERT する。画像は選んだ時点でストレージへ上げ URL を下書きに持つ。下書きは draftId で識別し、
 *   フォームの内容は sessionStorage (userUid:organizationId:draftId)、読み取りジョブの復帰情報は localStorage (同じキー) に持つ。
 *   登録成功・明示キャンセルで消す。単なる離脱では残す (同じタブの戻る・リロードで戻せる)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { brandConfig } from '../../config/brands';
import { useCreateProduct } from '../../features/products/hooks';
import {
  clearProductDraft,
  draftToCreateRequest,
  emptyProductDraft,
  isProductDraftEmpty,
  loadProductDraft,
  newDraftId,
  saveProductDraft,
  type ProductDraft,
  type ProductDraftForm,
} from '../../features/products/productDraft';
import { clearUrlSummaryResume, loadUrlSummaryResume, type UrlSummaryBrandMatch, type UrlSummaryBrandSiteCandidate, type UrlSummaryFormSnapshot, type UrlSummaryNewBrandDraft } from '../../features/products/urlSummary';
import { useUrlSummaryResumeScope } from '../../features/products/useUrlSummaryResumeScope';
import { useComplianceSettings } from '../../features/compliance/api';
import { apiClient } from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { Button } from '../../components/common/Button';
import { BackButton } from '../../components/common/BackButton';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { CreateBrandModal } from '../../components/generation/scenario/CreateBrandModal';
import { ProductUrlSummaryPanel, type ProductUrlSummaryApply, type ProductUrlSummaryPanelHandle, type UrlSummaryStepNo } from './shared/ProductUrlSummaryPanel';
import { ProductUrlSummaryModal, UrlSummaryLauncher } from './shared/ProductUrlSummaryModal';
import { ProductEntryChoices } from './shared/ProductEntryChoices';
import type { ProductEntryState } from './shared/ProductEntryModal';
import { ProductDetailsSection } from './shared/ProductDetailsSection';
import { ProductInfoEditTable } from './shared/ProductInfoEditTable';
import { BrandFieldHelp } from './shared/BrandFieldHelp';
import { DraftProductImagesSection } from './shared/DraftProductImagesSection';
import '../../features/products/industry.css';
import './ProductsPageHeader.css';
import './ProductDetailPage.css';
import './ManualProductNewPage.css';

type View = 'entry' | 'reading' | 'form';
type Route = 'manual' | 'url';

interface BrandCreatePrompt {
  brand_name: string;
  tagline: string;
  description: string;
  /** 読み取りから推定した公式サイトの候補 (CreateBrandModal の URL 欄の初期値と選択肢) */
  site_url_candidates: UrlSummaryBrandSiteCandidate[];
}

/**
 * 案件一覧の 2 択モーダルから来たときの最初の表示 (持ち主の取得を待たずに決める)。
 * URL 経路はパネルが同じ draftId の引き継ぎ情報からジョブの続き (完了済みなら結果) を受け取る。
 * 「読み取りを待たずに入力を始める」で来たとき (wait=false) はフォームを開いたまま待つ
 */
function initialEntryView(state: ProductEntryState | null): View {
  if (!state) return 'entry';
  if (state.entry === 'manual') return 'form';
  return state.wait === false ? 'form' : 'reading';
}

export default function ManualProductNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = useUrlSummaryResumeScope();
  const scopeKey = scope ? `${scope.userUid}:${scope.organizationId ?? ''}` : null;
  const productLabel = brandConfig.labels.productShort;
  useDocumentTitle(`${productLabel}を登録`);
  const createProduct = useCreateProduct();
  const { data: complianceSettings } = useComplianceSettings();

  // 案件一覧の 2 択モーダル (ProductEntryModal) で選んだ経路 (遷移の state)
  const location = useLocation();
  const entryState = (location.state as ProductEntryState | null) ?? null;

  // draftId: URL クエリ ?draft= に持つ。無ければ発行して置き換える (リロード・戻るで同じ下書きに戻れる)。
  // 置き換えると遷移の state が消えるので、2 択モーダルで選んだ経路は引き継ぐ
  const draftIdParam = searchParams.get('draft');
  useEffect(() => {
    if (draftIdParam) return;
    const next = new URLSearchParams(searchParams);
    next.set('draft', newDraftId());
    setSearchParams(next, { replace: true, state: location.state });
  }, [draftIdParam, searchParams, setSearchParams, location.state]);
  const draftId = draftIdParam ?? '';
  const presetBrandId = searchParams.get('brand_id') ?? '';

  const [draft, setDraft] = useState<ProductDraft>(() => emptyProductDraft(draftId || 'pending'));
  // 下書きの持ち主 (scopeKey)。保存はこれが現在の持ち主と一致するときだけ行う。持ち主が変わった直後のレンダーでは
  // draft がまだ前の利用者の内容なので、state で持って同じコミットの保存 Effect に前の内容を新しいキーへ書かせない
  const [draftOwner, setDraftOwner] = useState<string | null>(null);
  const [view, setView] = useState<View>(() => initialEntryView(entryState));
  const [route, setRoute] = useState<Route>(entryState?.entry === 'url' ? 'url' : 'manual');
  const [entryUrl, setEntryUrl] = useState(entryState?.entry === 'url' ? entryState.url : '');
  const [restored, setRestored] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [brandPrompt, setBrandPrompt] = useState<BrandCreatePrompt | null>(null);
  const [brandModal, setBrandModal] = useState<{ open: boolean; draft: BrandCreatePrompt | null }>({ open: false, draft: null });

  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await apiClient.get('/brands')).data,
    staleTime: 5 * 60 * 1000,
  });
  const brands = useMemo<{ id: string; brand_name: string }[]>(() => brandsData?.brands ?? [], [brandsData]);

  // ---- 復元 (同じタブのリロード・戻る) と、持ち主 (ユーザー / 組織) が変わったときのリセット ----
  const initializedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!scope || !draftId) return;
    const key = `${scopeKey}:${draftId}`;
    if (initializedForRef.current === key) return;
    // 同じ draftId のまま持ち主 (scopeKey) だけが変わった = ユーザー / 組織の切り替え
    const switchedOwner = initializedForRef.current !== null && initializedForRef.current.endsWith(`:${draftId}`);
    initializedForRef.current = key;
    setDraftOwner(scopeKey);
    const stored = loadProductDraft(scope, draftId);
    if (stored) {
      setDraft(stored);
      setView('form');
      setRoute(stored.sourcePages ? 'url' : 'manual');
      setRestored(true);
      return;
    }
    if (switchedOwner) {
      // 同じタブで別のユーザー / 組織に切り替わった: 前の所属先の下書きは出さない
      setDraft(emptyProductDraft(draftId));
      setView('entry');
      setRoute('manual');
      setBrandPrompt(null);
      toast.success('利用者が切り替わったため、入力中の内容をリセットしました');
      return;
    }
    // 新しい下書き: クエリの初期値 (シナリオ画面からの導線) とブランド指定 (ブランド詳細からの導線・入口を飛ばす)
    const preset: Partial<ProductDraftForm> = {};
    for (const key of ['title', 'description', 'brand_name'] as const) {
      const v = searchParams.get(key);
      if (v) preset[key] = v;
    }
    if (presetBrandId) {
      preset.brand_id = presetBrandId;
      const b = brands.find((x) => x.id === presetBrandId);
      if (b) preset.brand_name = b.brand_name;
    }
    setDraft(emptyProductDraft(draftId, preset));
    if (presetBrandId) {
      setView('form');
      setRoute('manual');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 持ち主と draftId が決まったときだけ
  }, [scopeKey, draftId]);

  // ブランド指定で開いたが一覧が後から届いたとき、自由入力の名前を補う
  useEffect(() => {
    if (!presetBrandId || draft.form.brand_name) return;
    const b = brands.find((x) => x.id === presetBrandId);
    if (b) setDraft((prev) => (prev.form.brand_id === presetBrandId && !prev.form.brand_name ? { ...prev, form: { ...prev.form, brand_name: b.brand_name } } : prev));
  }, [brands, presetBrandId, draft.form.brand_name]);

  // ---- 保存 (入力のたびに sessionStorage へ) ----
  useEffect(() => {
    if (!scope || !draftId || draft.draftId !== draftId) return;
    if (draftOwner !== scopeKey) return; // 持ち主が切り替わった直後 (前の利用者の内容) は保存しない
    if (view === 'entry') return;
    if (isProductDraftEmpty(draft)) {
      clearProductDraft(scope, draftId);
      return;
    }
    saveProductDraft(scope, draft);
  }, [draft, scope, scopeKey, draftId, view, draftOwner]);

  const updateForm = (form: ProductDraftForm) => setDraft((prev) => ({ ...prev, form }));

  // ---- URL 読み取り (パネル / モーダル共通) ----
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const getSummaryForm = useCallback((): UrlSummaryFormSnapshot => {
    const f = draftRef.current.form;
    return {
      title: f.title,
      description: f.description,
      brand_name: f.brand_name,
      brand_id: f.brand_id,
      product_type: f.product_type,
      category: f.category,
      industry_categories: f.industry_categories,
    };
  }, []);

  const brandPromptFrom = (match: UrlSummaryBrandMatch | null | undefined, currentBrandId: string): BrandCreatePrompt | null => {
    if (!match || currentBrandId) return null;
    const known = match.candidates.filter((c) => brands.some((b) => b.id === c.brand_id));
    if (match.status !== 'none' && known.length > 0) return null;
    const d = match.new_brand_draft;
    if (!d?.brand_name?.trim()) return null;
    return { brand_name: d.brand_name.trim(), tagline: d.tagline ?? '', description: d.description ?? '', site_url_candidates: d.official_site_candidates ?? [] };
  };

  const applySummary = useCallback((apply: ProductUrlSummaryApply) => {
    const { adoption, sourcePages, brand, brandMatch, auto } = apply;
    setDraft((prev) => {
      const { industry_categories, ...fields } = adoption.fields;
      const form: ProductDraftForm = {
        ...prev.form,
        ...fields,
        ...(industry_categories ? { industry_categories } : {}),
        ...(brand ? { brand_id: brand.brand_id, brand_name: brand.brand_name } : {}),
      };
      return {
        ...prev,
        form,
        attributesPatch: { ...prev.attributesPatch, ...adoption.attributes },
        sourcePages: sourcePages ?? prev.sourcePages,
      };
    });
    const nextBrandId = brand?.brand_id || draftRef.current.form.brand_id;
    setBrandPrompt(brandPromptFrom(brandMatch, nextBrandId));
    if (auto) setView('form');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- brands は brandPromptFrom が参照する
  }, [brands]);

  // ---- 入口 ----
  const startUrlRoute = (url: string) => {
    setEntryUrl(url);
    setRoute('url');
    setView('reading');
  };

  const startManualRoute = () => {
    setRoute('manual');
    setView('form');
  };

  // ---- ブランド作成 ----
  const openBrandModal = (initial: BrandCreatePrompt | null) => setBrandModal({ open: true, draft: initial });
  const brandPromptOf = (d: UrlSummaryNewBrandDraft): BrandCreatePrompt => ({ brand_name: d.brand_name, tagline: d.tagline, description: d.description, site_url_candidates: d.official_site_candidates ?? [] });
  const handleBrandCreated = (brandId: string, brand?: { brand_name: string }) => {
    const name = brand?.brand_name || brands.find((b) => b.id === brandId)?.brand_name || draftRef.current.form.brand_name;
    setDraft((prev) => ({ ...prev, form: { ...prev.form, brand_id: brandId, brand_name: name } }));
    setBrandPrompt(null);
  };

  // ---- 登録 / キャンセル ----
  const handleSubmit = async () => {
    const errors = new Set<string>();
    if (!draft.form.title.trim()) errors.add('title');
    setValidationErrors(errors);
    if (errors.size > 0) return;
    try {
      const created = await createProduct.mutateAsync(draftToCreateRequest(draft));
      if (scope) {
        clearProductDraft(scope, draftId);
        clearUrlSummaryResume(scope, draftId);
      }
      toast.success(`${productLabel}を登録しました`);
      navigate(`/products/manual/${created.id}`, { replace: true });
    } catch (err) {
      toast.error(`登録に失敗しました: ${getErrorMessage(err)}`);
    }
  };

  const requestCancel = () => {
    if (isProductDraftEmpty(draft)) {
      discardAndLeave();
      return;
    }
    setCancelConfirmOpen(true);
  };

  const discardAndLeave = () => {
    if (scope && draftId) {
      clearProductDraft(scope, draftId);
      clearUrlSummaryResume(scope, draftId);
    }
    setCancelConfirmOpen(false);
    navigate('/products/manual');
  };

  const isSaving = createProduct.isPending;
  const busy = isSaving || uploading;
  const panelRef = useRef<ProductUrlSummaryPanelHandle>(null);
  const [panelStep, setPanelStep] = useState<UrlSummaryStepNo>(1);
  const resumePending = !!(scope && draftId && loadUrlSummaryResume(scope, draftId));

  const brandNote = brandPrompt ? (
    <div className="new-product-brand-prompt" role="status" data-testid="new-product-brand-prompt">
      <span>
        「{brandPrompt.brand_name}」はブランドに未登録です。新規登録して{productLabel}に紐づけますか？
      </span>
      <span className="new-product-brand-prompt-actions">
        <button type="button" className="btn-modal-secondary-sm btn-quiet" onClick={() => setBrandPrompt(null)}>あとで</button>
        <button type="button" className="btn-modal-sm" onClick={() => openBrandModal(brandPrompt)}>ブランドを新規登録</button>
      </span>
    </div>
  ) : null;

  const renderEntry = () => (
    <ProductEntryChoices
      disabled={!scope}
      initialUrl={entryUrl}
      onStartUrl={startUrlRoute}
      onStartManual={startManualRoute}
      onResume={resumePending ? () => { setRoute('url'); setView('reading'); } : undefined}
    />
  );

  const showPanel = route === 'url' && view !== 'entry';
  const showForm = view === 'form';
  // 「別のURLで読み直す」は反映後 (パネルの段階2) だけ、戻るの下に同じ見た目で出す (利用者指示 2026-10-30)。
  // 戻るはページを離れる操作のまま (段階1 には戻さない)。読み直しはパネルの段階1 へ戻し、入れたフォームはそのまま
  const showReread = showPanel && panelStep === 2;

  return (
    <div className={`product-detail-page new-product-page${showReread ? ' new-product-page--reread' : ''}`}>
      <div className="header-actions">
        <BackButton />
        {showReread && (
          <BackButton
            label="別のURLで読み直す"
            data-testid="new-product-reread"
            onClick={(e) => { e.preventDefault(); panelRef.current?.reread(); }}
          />
        )}
      </div>
      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">{productLabel}を登録</h1>
          {view === 'entry' && <p className="page-description products-tab-description">登録方法を選んでください</p>}
          {restored && showForm && <p className="page-description">入力途中の内容を復元しました</p>}
        </div>
      </header>

      {view === 'entry' && renderEntry()}

      {showPanel && draftId && (
        <ProductUrlSummaryPanel
          draftId={draftId}
          hideTitle
          getForm={getSummaryForm}
          brands={brands}
          onApply={applySummary}
          onCreateBrand={(d) => openBrandModal(brandPromptOf(d))}
          disabled={busy}
          initialUrl={entryUrl || undefined}
          autoStart={view === 'reading' && !!entryUrl}
          autoApply
          ref={panelRef}
          onStepChange={setPanelStep}
          hideRereadAction
        />
      )}
      {view === 'reading' && (
        <div className="new-product-reading-actions">
          {/* 読み取りは続けたまま入力フォームを開く (完了すれば未入力の項目に自動で入る) */}
          <button type="button" className="new-product-text-link" onClick={() => setView('form')}>
            <LogOut size={14} aria-hidden="true" />
            読み取りを待たずに入力を始める
          </button>
        </div>
      )}

      {showForm && (
        <section className="product-result">
          <DraftProductImagesSection
            images={draft.images}
            onChange={(update) => setDraft((prev) => ({ ...prev, images: update(prev.images) }))}
            title={draft.form.title}
            disabled={isSaving}
            onUploadingChange={setUploading}
          />
          <ProductDetailsSection
            title={`${productLabel}情報`}
            headerActions={route === 'manual' ? (
              <UrlSummaryLauncher onClick={() => setSummaryOpen(true)} pending={resumePending} disabled={isSaving} />
            ) : undefined}
            footer={(
              <>
                <Button variant="danger" size="sm" onClick={requestCancel} disabled={isSaving}>
                  キャンセル
                </Button>
                <Button variant="success" size="sm" className="btn-modal-sub" onClick={() => void handleSubmit()} disabled={busy} data-testid="new-product-submit">
                  {isSaving ? '登録中...' : uploading ? '画像をアップロード中...' : '登録する'}
                </Button>
              </>
            )}
          >
            <table className="details-table">
              <tbody>
                <ProductInfoEditTable
                  editForm={draft.form}
                  setEditForm={updateForm}
                  validationErrors={validationErrors}
                  brands={brands}
                  complianceSettings={complianceSettings}
                  onCreateBrand={() => openBrandModal(null)}
                  brandFirst
                  brandHelp={<BrandFieldHelp />}
                  brandNote={brandNote}
                />
              </tbody>
            </table>
          </ProductDetailsSection>
        </section>
      )}

      {route === 'manual' && draftId && (
        <ProductUrlSummaryModal
          open={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          draftId={draftId}
          getForm={getSummaryForm}
          brands={brands}
          onApply={applySummary}
          onCreateBrand={(d) => openBrandModal(brandPromptOf(d))}
          disabled={isSaving}
        />
      )}

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        variant="warning"
        title={`${productLabel}の登録をやめますか？`}
        message="入力した内容と選んだ画像は破棄されます。"
        confirmLabel="破棄する"
        cancelLabel="入力を続ける"
        onConfirm={discardAndLeave}
        onCancel={() => setCancelConfirmOpen(false)}
      />

      <CreateBrandModal
        isOpen={brandModal.open}
        onClose={() => setBrandModal((prev) => ({ ...prev, open: false }))}
        onBrandCreated={handleBrandCreated}
        initialValues={brandModal.draft}
        startAt={brandModal.draft ? 'confirm' : 'url'}
        confirmLabel={`登録して${productLabel}に紐づける`}
      />
    </div>
  );
}
